import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CubeIcon,
  TrendingUpIcon,
  DocumentDownloadIcon,
  CalendarIcon,
  HomeIcon,
  UserIcon,
  ClipboardListIcon,
} from "@heroicons/react/outline";
import { reportsAPI } from "../api/reports";
import { projectsAPI } from "../api/projects";
import LoadingSpinner from "../components/common/LoadingSpinner";
import toast from "react-hot-toast";

const ReportsPage = () => {
  const [selectedProject, setSelectedProject] = useState("");
  const [reportType, setReportType] = useState("cost");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [isExporting, setIsExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [generated, setGenerated] = useState(false);

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projectsAPI.getAll(),
  });

  const generateMutation = useMutation({
    mutationFn: () => {
      switch (reportType) {
        case "cost":
          return reportsAPI.getProjectCost(selectedProject);
        case "labor":
          return reportsAPI.getLabor(selectedProject, dateRange);
        case "material":
          return reportsAPI.getMaterial(selectedProject, dateRange);
        case "progress":
          return reportsAPI.getProgress(selectedProject);
        default:
          return Promise.reject("Invalid report type");
      }
    },
    onSuccess: (data) => {
      setReportData(data.data);
      setGenerated(true);
      toast.success("Report generated successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to generate report",
      );
    },
  });

  const projects = projectsData?.data?.projects || [];

  const reportTypes = [
    {
      id: "cost",
      label: "Cost Report",
      icon: CurrencyDollarIcon,
      color: "text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20",
    },
    {
      id: "labor",
      label: "Labor Report",
      icon: UsersIcon,
      color: "text-blue-500 bg-blue-50 dark:bg-blue-900/20",
    },
    {
      id: "material",
      label: "Material Report",
      icon: CubeIcon,
      color: "text-orange-500 bg-orange-50 dark:bg-orange-900/20",
    },
    {
      id: "progress",
      label: "Progress Report",
      icon: TrendingUpIcon,
      color: "text-purple-500 bg-purple-50 dark:bg-purple-900/20",
    },
  ];

  const handleGenerate = () => {
    if (!selectedProject) {
      toast.error("Please select a project");
      return;
    }
    setGenerated(false);
    generateMutation.mutate();
  };

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      toast.success("Report exported successfully");
      setIsExporting(false);
    }, 2000);
  };

  if (projectsLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            📊 Reports
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Generate and view detailed project reports
          </p>
        </div>
        {reportData && generated && (
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="btn-secondary flex items-center gap-2"
          >
            <DocumentDownloadIcon className="h-5 w-5" />
            {isExporting ? "Exporting..." : "Export Report"}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              <HomeIcon className="h-4 w-4 inline mr-1" />
              Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setGenerated(false);
                setReportData(null);
              }}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              <ChartBarIcon className="h-4 w-4 inline mr-1" />
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                setGenerated(false);
                setReportData(null);
              }}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            >
              {reportTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              <CalendarIcon className="h-4 w-4 inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              <CalendarIcon className="h-4 w-4 inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleGenerate}
            disabled={!selectedProject || generateMutation.isLoading}
            className="btn-primary flex items-center gap-2 px-6 py-2.5"
          >
            <ChartBarIcon className="h-5 w-5" />
            {generateMutation.isLoading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      {/* Report Display */}
      {generateMutation.isLoading ? (
        <div className="text-center py-12">
          <LoadingSpinner />
          <p className="text-slate-600 dark:text-slate-400 mt-4">
            Generating your report...
          </p>
        </div>
      ) : selectedProject && reportData && generated ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Report Header */}
          <div className="card bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/10 border-primary-200 dark:border-primary-800">
            <div className="flex items-center gap-3">
              <div
                className={`p-3 rounded-xl ${reportTypes.find((t) => t.id === reportType)?.color}`}
              >
                {reportTypes.find((t) => t.id === reportType)?.icon &&
                  React.createElement(
                    reportTypes.find((t) => t.id === reportType).icon,
                    { className: "h-6 w-6" },
                  )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  {reportTypes.find((t) => t.id === reportType)?.label}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Project: {reportData.project?.name || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Render different report types */}
          {reportType === "cost" && <CostReport data={reportData} />}
          {reportType === "labor" && <LaborReport data={reportData} />}
          {reportType === "material" && <MaterialReport data={reportData} />}
          {reportType === "progress" && <ProgressReport data={reportData} />}
        </motion.div>
      ) : selectedProject ? (
        <div className="text-center py-12">
          <ChartBarIcon className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            Click "Generate Report" to view data
          </p>
        </div>
      ) : (
        <div className="text-center py-12">
          <ChartBarIcon className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            Select a project to generate reports
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// COST REPORT COMPONENT
// ============================================
const CostReport = ({ data }) => {
  const { project, summary, stage_costs, material_costs, labor_costs } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Budget
          </p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            ${parseFloat(project.total_budget).toLocaleString()}
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Current Spent
          </p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            ${parseFloat(project.current_spent).toLocaleString()}
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Remaining
          </p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            ${parseFloat(project.remaining_budget).toLocaleString()}
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Budget Used
          </p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {project.total_budget > 0
              ? Math.round((project.current_spent / project.total_budget) * 100)
              : 0}
            %
          </p>
        </div>
      </div>

      {stage_costs && stage_costs.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            📋 Stage Costs
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Stage
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Estimated
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Actual
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Variance
                  </th>
                </tr>
              </thead>
              <tbody>
                {stage_costs.map((stage, index) => {
                  const variance = parseFloat(stage.variance);
                  const isOverBudget = variance < 0;

                  return (
                    <tr
                      key={index}
                      className="border-b border-slate-100 dark:border-slate-700/50"
                    >
                      <td className="py-3 px-4 text-sm text-slate-900 dark:text-white">
                        {stage.stage_name}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                        ${parseFloat(stage.estimated_cost).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                        ${parseFloat(stage.actual_cost).toLocaleString()}
                      </td>
                      <td
                        className={`py-3 px-4 text-sm text-right font-medium ${
                          isOverBudget
                            ? "text-red-600 dark:text-red-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        {isOverBudget ? "-" : ""}$
                        {Math.abs(parseFloat(stage.variance)).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// LABOR REPORT COMPONENT
// ============================================
const LaborReport = ({ data }) => {
  const { summary, workers } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Workers
          </p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {summary?.total_workers || 0}
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Hours
          </p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {summary?.total_hours || 0}h
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Overtime Hours
          </p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {summary?.total_overtime || 0}h
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Cost
          </p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            ${summary?.total_cost?.toLocaleString() || "0"}
          </p>
        </div>
      </div>

      {workers && workers.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            👷 Worker Summary
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Worker
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Days
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Hours
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Total Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {workers.map((worker, index) => (
                  <tr
                    key={index}
                    className="border-b border-slate-100 dark:border-slate-700/50"
                  >
                    <td className="py-3 px-4 text-sm text-slate-900 dark:text-white">
                      {worker.worker_name}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                      {worker.days_worked || 0}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                      {worker.total_hours || 0}h
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-slate-900 dark:text-white text-right">
                      ${worker.total_cost?.toLocaleString() || "0"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// MATERIAL REPORT COMPONENT
// ============================================
const MaterialReport = ({ data }) => {
  const { material_usage } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Materials
          </p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {material_usage?.length || 0}
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Cost
          </p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            $
            {material_usage
              ?.reduce((sum, m) => sum + parseFloat(m.total_cost || 0), 0)
              ?.toLocaleString() || "0"}
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Quantity
          </p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {material_usage?.reduce(
              (sum, m) => sum + parseFloat(m.total_consumed || 0),
              0,
            ) || 0}
          </p>
        </div>
      </div>

      {material_usage && material_usage.length > 0 ? (
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            🧱 Material Usage
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Material
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Unit
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Quantity
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Total Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {material_usage.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b border-slate-100 dark:border-slate-700/50"
                  >
                    <td className="py-3 px-4 text-sm text-slate-900 dark:text-white">
                      {item.material_name}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                      {item.unit || "pcs"}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                      {parseFloat(item.total_consumed || 0).toFixed(0)}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-slate-900 dark:text-white text-right">
                      ${parseFloat(item.total_cost || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card text-center py-8">
          <p className="text-slate-500 dark:text-slate-400">
            No material usage data available
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// PROGRESS REPORT COMPONENT
// ============================================
const ProgressReport = ({ data }) => {
  const { progress, details, stages } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Stage Progress
          </p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {progress?.stage_progress || 0}%
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Task Progress
          </p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {progress?.task_progress || 0}%
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Budget Utilization
          </p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {progress?.budget_utilization || 0}%
          </p>
        </div>
        <div className="card bg-white dark:bg-slate-800/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Time Progress
          </p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {progress?.time_progress || 0}%
          </p>
        </div>
      </div>

      {details && (
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            📋 Project Details
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Total Stages
              </p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {details.total_stages || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Completed Stages
              </p>
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {details.completed_stages || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Total Tasks
              </p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {details.total_tasks || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Completed Tasks
              </p>
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {details.completed_tasks || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {stages && stages.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            📊 Stage Progress
          </h3>
          <div className="space-y-4">
            {stages.map((stage, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 dark:text-slate-300">
                    {stage.stage_name}
                  </span>
                  <span className="text-slate-600 dark:text-slate-400">
                    {stage.completion_percentage || 0}%
                  </span>
                </div>
                <div className="mt-1 w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                    style={{ width: `${stage.completion_percentage || 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
